import { Destroyer, isEmptyInput, validatePassword } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { UiService } from '@adaptiva/core/ui';
import { AuthenticationService, encryptPassword, SystemConfigService } from '@adaptiva/data-service';
import { ButtonComponent } from '@adaptiva/widgets/button';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, filter, fromEvent } from 'rxjs';

@Component({
	standalone: false,
	selector: 'ada-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordComponent extends Destroyer implements OnDestroy, AfterViewInit, OnInit {

	@ViewChild('ResetButton')
	private _resetButton: ButtonComponent;

	public userEmail: string;

	public userPassword: string;

	public userConfirmPassword: string;

	public errorMessage: BehaviorSubject<string> = new BehaviorSubject(null);

	public passwordError: BehaviorSubject<string> = new BehaviorSubject('');

	protected readonly confirmPasswordError: WritableSignal<string> = signal('');

	protected readonly systemConfig: SystemConfigService = inject(SystemConfigService);

	@HostBinding('style')
	protected readonly hostStyles: Record<string, string> = { 'max-width': '30rem' };

	@HostBinding('class')
	protected readonly hostCssClasses: string[] = ['ada-container', 'ada-flex-align-center'];

	private _token: string;

	private _isResetting: boolean = false;

	constructor(
		public cd: ChangeDetectorRef,
		public router: Router,
		public ui: UiService,
		protected auth: AuthenticationService,
		protected log: LoggingService,
		protected route: ActivatedRoute,
		protected snackbarService: SnackbarService
	) {
		super();
	}

	public ngOnInit(): void {
		this._token = this.route.snapshot.queryParams.token;

		if (isEmptyInput(this._token)) {
			this.router.navigateByUrl('/login;resetPassword=true').then(() => {
				this.snackbarService.display({
					message: 'Invalid reset token provided. Please try again.',
					dismissable: true,
					duration: 0,
				});
			});
		}
	}

	public ngAfterViewInit(): void {
		this._listenForEnter();
	}

	public checkPasswordError(_password: string): void {
		this.passwordError.next(validatePassword(_password));
	}

	public checkPasswordMatch(): void {
		if (this.userConfirmPassword === this.userPassword)
			this.confirmPasswordError.set('');
		else
			this.confirmPasswordError.set('Passwords do not match');
	}

	public reset(): void {
		if (this._isResetting)
			return;

		this._resetButton.isLoading = true;
		this._resetButton.isDisabled = true;

		if (!this._isValidCredentials()) {
			this._resetButton.isLoading = false;
			this._resetButton.isDisabled = false;

			return;
		}

		const _username: string = this.userEmail.toLowerCase();
		const _password: string = encryptPassword(this.userPassword, _username);

		this._isResetting = true;

		this.auth.resetPassword({
			username: _username,
			newPassword: _password
		}, this._token).pipe(
			this.takeOneUntilDestroyed(),
		).subscribe({
			next: this._handleResetResult.bind(this),
			error: this._handleResetError.bind(this)
		});
	}

	private _handleResetError(_error: Error): void {
		const _title: string = 'Unexpected failure during login. Check browser console for details.';
		const _message: string = this.log.extractErrorMessage(_error);

		this.log.error(`ResetPasswordComponent._handleResetError ${_title}. ${_message}`, _error)();
		this.errorMessage.next(_message);

		this._resetButton.isLoading = false;
		this._resetButton.isDisabled = false;
		this._isResetting = false;
	}

	private _handleResetResult(): void {
		this._resetButton.isLoading = false;
		this._resetButton.text = '';
		this._resetButton.icon = 'faCheck';
		this._resetButton.look = 'solid';
		this._resetButton.type = 'success';

		this.errorMessage.next(null);
		this.router.navigateByUrl('/');
	}

	private _isValidCredentials(): boolean {
		const _validationError: string = validatePassword(this.userPassword);

		if (_validationError) {
			this.errorMessage.next(_validationError);

			return false;
		}

		if (this.userPassword !== this.userConfirmPassword) {
			this.errorMessage.next('The passwords do not match');

			return false;
		}

		this.errorMessage.next('');

		return true;
	}

	private _listenForEnter(): void {
		fromEvent(document, 'keydown').pipe(
			filter((_event: KeyboardEvent) => _event.key === 'Enter'),
			this.takeUntilDestroyed()
		).subscribe(() => {
			if (this._isValidCredentials())
				this.reset();
		});
	}

}

